import React from 'react';
import { LiveProductPreview } from './LiveProductPreview';
import Page2ArtworkSection from './Page2ArtworkSection';
import Page2ProgressMeter from './Page2ProgressMeter';
import CombinedSizeQuantity from './CombinedSizeQuantity';
import { PriceEstimateBanner } from './PriceEstimateBanner';
import { SaveQuoteButton } from '../../components/SaveQuoteButton';
import { isWhiteInkEligible } from '../../lib/whiteInkEligibility';
import { MockupIdleDropzone } from './MockupIdleDropzone';
import { useArtworkUpload } from '../../hooks/useArtworkUpload';

/**
 * Wizard Page 2 — the combined Size + Quantity + Live AI Preview panel.
 *
 * Renders on step 4 OR 5: the internal 6-step state machine is preserved
 * (validators, foil price hooks, page-maker detection, special-quote
 * gates all key off `step`) but the panel keeps size + qty + live price
 * on screen simultaneously so tweaks feel instant.
 *
 * Purely presentational — the parent QuoteCalculator owns all state and
 * pricing callbacks; this component just wires them into the right
 * child layout.
 */
export const WizardPage2Panel = ({
  // Product context (for LiveProductPreview + white-ink gate)
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  getAvailableMaterials,
  backendCombo,
  // Size + quantity state
  selectedShape,
  setSelectedShape,
  width,
  setWidth,
  height,
  setHeight,
  unit,
  setUnit,
  quantity,
  setQuantity,
  customQuantity,
  setCustomQuantity,
  // Page 2 artwork collector
  page2ArtworkFile,
  setPage2ArtworkFile,
  page2ArtworkId,
  setPage2ArtworkId,
  page2CutlineShape,
  setPage2CutlineShape,
  page2ArtworkInstructions,
  setPage2ArtworkInstructions,
  // White ink (eligible products only)
  whiteInkOption,
  setWhiteInkOption,
  // Pricing / progress derivations
  subtotal,
  total,
  backendPriceExtrapolated,
  backendRequiresCustomQuote,
  // Callbacks
  checkSizeLimit,
  handleCustomQuoteRequest,
  requiresCustomQuoteAtQuantity,
  getQuantityConfig,
  calculateItemsPerPage,
  calculatePrice,
  formatCurrency,
  // Auth wiring
  isAuthenticated,
  onRequireAuth,
  refreshAuth,
  // Save quote
  saveQuoteDraftUrl,
  saveQuoteProductName,
  currency,
}) => {
  // iter114p — own the artwork upload pipeline at this level so the
  // empty-state dropzone (mounted inside `LiveProductPreview`'s
  // idleSlot) AND the `Page2ArtworkSection` selected-file UI both
  // drive the same upload flow.
  const { handleFile: handleArtworkFile, uploading: artworkUploading, error: artworkError, clear: clearArtwork } = useArtworkUpload({
    artworkFile: page2ArtworkFile,
    setArtworkFile: setPage2ArtworkFile,
    artworkId: page2ArtworkId,
    setArtworkId: setPage2ArtworkId,
    isAuthenticated,
    onRequireAuth,
    refreshAuth,
  });

  return (
  <>
    {/* 1. HERO Live AI Preview — pinned to the top of Page 2.
        Renders the customer's actual artwork the moment they drop a
        file below (canGenerate is satisfied as soon as product +
        material + artworkId are present; size/qty use fallbacks until
        the customer picks them).

        Page 2 specifically nests the "Upload your artwork" panel
        INSIDE the preview card (via heroSlot) so the two concepts —
        feed artwork + see the live mockup — read as one cohesive step.
    */}
    <div className="mt-4">
      <LiveProductPreview
        hero
        productType={selectedProduct}
        material={
          (getAvailableMaterials().find((m) => m.id === selectedMaterial)?.name) ||
          selectedMaterial
        }
        formatName={selectedFormat}
        width={width}
        height={height}
        unit={unit}
        quantity={quantity}
        artworkId={page2ArtworkId}
        artworkFile={page2ArtworkFile}
        cutlineShape={page2CutlineShape}
        whiteInkOption={whiteInkOption}
        scene={backendCombo}
        isAuthenticated={isAuthenticated}
        onRequireAuth={onRequireAuth}
        progressSlot={
          <Page2ProgressMeter
            step1Done={!!page2ArtworkId}
            step2Done={
              Number(width) > 0 &&
              Number(height) > 0 &&
              Number(parseInt(quantity, 10)) > 0
            }
            step3Done={subtotal > 0 && Number(total) > 0}
          />
        }
        idleSlot={
          <MockupIdleDropzone onFile={handleArtworkFile} />
        }
        heroSlot={
          <Page2ArtworkSection
            nested
            artworkFile={page2ArtworkFile}
            setArtworkFile={setPage2ArtworkFile}
            artworkId={page2ArtworkId}
            setArtworkId={setPage2ArtworkId}
            cutlineShape={page2CutlineShape}
            setCutlineShape={setPage2CutlineShape}
            artworkInstructions={page2ArtworkInstructions}
            setArtworkInstructions={setPage2ArtworkInstructions}
            whiteInkOption={whiteInkOption}
            setWhiteInkOption={setWhiteInkOption}
            selectedFormat={selectedFormat}
            selectedMaterial={selectedMaterial}
            handleFile={handleArtworkFile}
            uploading={artworkUploading}
            error={artworkError}
            clear={clearArtwork}
            isAuthenticated={isAuthenticated}
            onRequireAuth={onRequireAuth}
            refreshAuth={refreshAuth}
          />
        }
      />
    </div>

    {/* 2. Price estimate badge (extrapolation / custom-quote hint) */}
    <div className="mt-4">
      <PriceEstimateBanner
        extrapolated={backendPriceExtrapolated}
        requiresCustomQuote={backendRequiresCustomQuote}
        onRequestCustomQuote={handleCustomQuoteRequest}
      />

      {/* 3. Compact Shape + Size + Quantity (with inline custom qty) */}
      <CombinedSizeQuantity
        selectedShape={selectedShape}
        setSelectedShape={setSelectedShape}
        width={width}
        setWidth={setWidth}
        height={height}
        setHeight={setHeight}
        unit={unit}
        setUnit={setUnit}
        checkSizeLimit={checkSizeLimit}
        handleCustomQuoteRequest={handleCustomQuoteRequest}
        whiteInkEligible={isWhiteInkEligible(selectedFormat, selectedMaterial)}
        whiteInkOption={whiteInkOption}
        setWhiteInkOption={setWhiteInkOption}
        requiresCustomQuoteAtQuantity={requiresCustomQuoteAtQuantity}
        selectedProduct={selectedProduct}
        selectedFormat={selectedFormat}
        getQuantityConfig={getQuantityConfig}
        calculateItemsPerPage={calculateItemsPerPage}
        quantity={quantity}
        setQuantity={setQuantity}
        customQuantity={customQuantity}
        setCustomQuantity={setCustomQuantity}
        calculatePrice={calculatePrice}
        formatCurrency={formatCurrency}
      />
    </div>

    {/* iter114s.17 — Bulk pricing ladder removed at customer request
        ("delete this section no longer needed"). The QuantityLadder
        component remains in the codebase in case a future bundle/upsell
        view wants to reuse it, but it is no longer rendered here. */}

    {/* 5. Save this quote — directly below Select Quantity. */}
    <div className="mt-4" data-testid="page2-save-quote-slot">
      <SaveQuoteButton
        draftUrl={saveQuoteDraftUrl}
        productName={saveQuoteProductName}
        totalPrice={total ? parseFloat(total) : 0}
        currency={currency}
      />
    </div>
  </>
  );
};

export default WizardPage2Panel;
